import React from 'react';
import { Box, Typography, Button, Paper, Chip } from '@mui/material';

interface GuessInputProps {
  guessError: string;
  isSubmitting: boolean;
  gameCompleted: boolean;
  score: number;
  onSubmit: (event: React.MouseEvent | React.FormEvent) => void;
  // Inline hints props
  showHintButton?: boolean;
  hintsUnlocked?: boolean;
  hasUnrevealedHints?: boolean;
  revealedHintsCount?: number;
  totalHintsCount?: number;
  onRevealHint?: () => void;
  isRevealingHint?: boolean;
}

const GuessInput: React.FC<GuessInputProps> = ({
  guessError,
  isSubmitting,
  gameCompleted,
  score,
  onSubmit,
  showHintButton = false,
  hintsUnlocked = false,
  hasUnrevealedHints = false,
  revealedHintsCount = 0,
  totalHintsCount = 0,
  onRevealHint,
  isRevealingHint = false
}) => {
  return (
    <Paper elevation={3} sx={{ px: { xs: 0.5, sm: 0.4, md: 1.1, lg: 2.5 }, py: { xs: 0.5, sm: 0.4, md: 1.1, lg: 2.5 }, mb: 2, textAlign: 'center' }}>
      {guessError && (
        <Typography
          variant="body1"
          sx={{
            color: 'error.main',
            mb: { xs: 1, sm: 1.25, md: 1.5 },
            fontWeight: 500,
            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem', lg: '1rem' }
          }}
        >
          {guessError}
        </Typography>
      )}

      {/* Help text when hints are locked */}
      {showHintButton && !hintsUnlocked && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: { xs: 1, sm: 1.25, md: 1.5 },
            fontStyle: 'italic',
            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.875rem' }
          }}
        >
          Ask all questions to reveal hints.
        </Typography>
      )}

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1, md: 1.5, lg: 2 }, justifyContent: 'center', mb: { xs: 1, sm: 1.25, md: 1.5 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={isSubmitting || gameCompleted}
          sx={{
            px: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
            py: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem' },
            flex: 1,
            maxWidth: { xs: 'none', lg: 200 }
          }}
        >
          Make Guess
        </Button>

        {showHintButton && (
          <>
            {!hintsUnlocked ? (
              <Button
                variant="outlined"
                size="large"
                disabled
                sx={{
                  px: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  py: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem' },
                  flex: 1,
                  maxWidth: { xs: 'none', lg: 200 },
                  borderColor: 'divider',
                  color: 'text.disabled'
                }}
              >
                Hints Locked
              </Button>
            ) : hasUnrevealedHints ? (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={onRevealHint}
                disabled={gameCompleted || isRevealingHint}
                sx={{
                  px: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  py: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem' },
                  flex: 1,
                  maxWidth: { xs: 'none', lg: 200 },
                  fontWeight: 'bold',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                {isRevealingHint ? 'Revealing...' : 'Reveal Hint'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="success"
                size="large"
                disabled
                sx={{
                  px: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  py: { xs: 0.75, sm: 0.5, md: 1, lg: 3 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem' },
                  flex: 1,
                  maxWidth: { xs: 'none', lg: 200 },
                  fontWeight: 'bold'
                }}
              >
                All Hints Used
              </Button>
            )}
          </>
        )}
      </Box>

      {/* Score and Hint Counter */}
      <Box sx={{ mt: { xs: 0.75, sm: 1 }, display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 0.75, md: 1 }, flexWrap: 'wrap' }}>
        <Chip
          label={`Score: ${score}`}
          color="primary"
          variant="outlined"
          size="medium"
          sx={{
            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '1rem' },
            fontWeight: 'bold'
          }}
        />

        {showHintButton && hintsUnlocked && (
          <Chip
            label={`Hints: ${revealedHintsCount}/${totalHintsCount}`}
            color="secondary"
            variant="outlined"
            size="medium"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '1rem' },
              fontWeight: 'bold'
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default GuessInput;